import { CSSProperties, useState } from 'react';
import { MemberView, OrderView } from './adapters';
import { MemberStage, STAGE_META, shortWon } from './tabData';
import { isFirstPass, needsClassification, isDormantGap, DORMANT_GAP_DAYS } from './passOrder';
import { summarizeRollup, ROLLUP_PARTIAL_NOTE } from './memberRollup';
import { VALUE_UNAVAILABLE_TEXT, valueUnavailableReason } from './memberValue';
import { isSessionless, SESSIONLESS_REMAIN_TEXT, SESSIONLESS_REMAIN_HINT } from './sessionless';
import { weeklyPaceText, paceTooltip } from './pace';
import {
    signalTags,
    isRemainAlert,
    isExpiryAlert,
    isGapAlert,
    isPaceAlert,
    isSameDayAdjustAlert,
    SAME_DAY_ADJUST_ALERT,
} from './memberSignals';
import { OrderStatus } from '../../types/opsBriefing';

/**
 * 회원 요약 한 줄.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 카드에서 줄로 바꿨나
 * ─────────────────────────────────────────────────────────────
 * 상세 카드는 회원 한 명당 세로 260px 을 씁니다. 12명이면 화면을 세 번 굴려야
 * 명단이 끝나고, 그러면 "누가 제일 급한가"를 눈으로 비교할 수가 없습니다.
 * 원장님이 이 화면에서 하는 일은 **고르는 것**이지 읽는 것이 아닙니다.
 *
 * 그래서 기본 화면은 한 줄짜리 요약으로 두고, 판단에 필요한 값은 전부 같은
 * 눈금 위에 세로로 정렬합니다 — 잔여·만료·공백·페이스·당일 취소·변경·금액이
 * 열마다 같은 자리에 있으면 위아래로 훑는 것만으로 비교가 됩니다.
 * 근거·추천 문구·지시 버튼이 있는 **상세 카드는 줄을 누르면 그대로** 열립니다.
 *
 * ⚠️ 지시 버튼은 이 줄에도 남깁니다. 상세를 열어야만 지시할 수 있게 하면
 *    목록에서 급한 회원 셋을 찾아 놓고도 세 번 열었다 닫아야 합니다.
 */

/**
 * 열 너비 — 머리글과 행이 **같은 상수**를 봐야 눈금이 어긋나지 않는다.
 * 한쪽만 고치면 숫자가 라벨과 한 칸씩 밀린다.
 */
export const SUMMARY_COLS = {
    coach: 150,
    remain: 62,
    expiry: 90,
    gap: 86,
    pace: 104,
    /** 노쇼율 자리를 이어받았다 — 라벨이 길어 칸도 넓다 */
    sameDay: 106,
    value: 124,
    order: 156,
    hide: 84,
} as const;

/**
 * 어느 칸을 그릴 수 있는가.
 *
 * ⚠️ **머리글과 행이 같은 값을 받아야 합니다.** 한쪽만 칸을 빼면 그 아래
 * 모든 숫자가 라벨과 한 칸씩 밀립니다 — 원장님은 만료일을 마지막 수업으로 읽습니다.
 * 그래서 판정은 화면(페이지)에서 한 번만 하고 둘 다에 같은 객체를 넘깁니다.
 */
export type SummaryCapabilities = {
    /** 서버가 당일 취소·변경을 보내는가 (구버전이면 칸 자체를 뺀다) */
    showSameDay: boolean;
    /** 서버가 회원 가리기를 아는가 (모르면 눌러서 404 나는 버튼을 만들지 않는다) */
    showHide: boolean;
};

/** 고정 칸(회원·강사·신호·잔여·만료·공백·페이스·금액·지시)만 세운 폭 */
const BASE_MIN_WIDTH = 1122;

/** 좁은 창에서는 열을 찌그러뜨리지 않고 가로로 넘긴다 */
export const summaryMinWidth = ({ showSameDay, showHide }: SummaryCapabilities): number =>
    BASE_MIN_WIDTH +
    (showSameDay ? SUMMARY_COLS.sameDay + 10 : 0) +
    (showHide ? SUMMARY_COLS.hide + 10 : 0);

const NAME_COL: CSSProperties = { flex: '1 1 196px', minWidth: 196 };
const SIGNAL_COL: CSSProperties = { flex: '1 1 210px', minWidth: 210 };

const HEAD: CSSProperties = {
    fontSize: 11.5,
    fontWeight: 700,
    color: '#93A0AE',
    letterSpacing: '0.02em',
    flex: 'none',
};

/** 목록 머리글 — 숫자만 늘어놓으면 무슨 숫자인지 모른다 */
export const MemberSummaryHeader = ({ showSameDay, showHide }: SummaryCapabilities) => (
    <div
        className="flex items-center"
        style={{
            padding: '9px 26px',
            background: '#F7F9FC',
            borderBottom: '1px solid #E3EAF3',
            gap: 10,
            minWidth: summaryMinWidth({ showSameDay, showHide }),
        }}
    >
        <span style={{ ...HEAD, ...NAME_COL }}>회원 · 수강권 순번</span>
        <span style={{ ...HEAD, width: SUMMARY_COLS.coach }}>담당 강사 · 상품</span>
        <span style={{ ...HEAD, ...SIGNAL_COL }}>지금 걸린 신호</span>
        <span style={{ ...HEAD, width: SUMMARY_COLS.remain, textAlign: 'right' }}>남은 수업</span>
        <span style={{ ...HEAD, width: SUMMARY_COLS.expiry, textAlign: 'right' }}>만료까지</span>
        <span style={{ ...HEAD, width: SUMMARY_COLS.gap, textAlign: 'right' }}>마지막 수업</span>
        <span style={{ ...HEAD, width: SUMMARY_COLS.pace, textAlign: 'right' }}>주당 수업</span>
        {/*
          노쇼율 자리다. 대표 지시로 회원 현황의 표시에서 노쇼율을 빼고
          당일 취소·변경으로 바꿨다. 서버는 noshowPct 를 그대로 보낸다 —
          여기서만 안 그린다.
        */}
        {showSameDay && (
            <span style={{ ...HEAD, width: SUMMARY_COLS.sameDay, textAlign: 'right' }}>
                당일 취소·변경
            </span>
        )}
        {/*
          🔴 「걸린 금액」에서 이름을 바꿨다 (2026-08-31 정책 M-D4·M8).
             그 칸의 숫자는 예전에도 지금도 대부분 **미사용 선수금**이었는데
             이름이 그걸 안 말해서, 원장님은 재등록 기대까지 포함된 총액으로 읽었다.
             재등록 기대는 아래 작은 줄로 따로 붙는다 — 더하지 않는다.
        */}
        <span
            style={{ ...HEAD, width: SUMMARY_COLS.value, textAlign: 'right' }}
            title="잔여 회차 × 회당 단가. 이미 받았지만 아직 수업하지 않은 금액입니다. 재등록 기대는 아래 줄에 따로 적습니다."
        >
            미사용 선수금
        </span>
        <span style={{ ...HEAD, width: SUMMARY_COLS.order, textAlign: 'right' }}>지시</span>
        {showHide && (
            <span style={{ ...HEAD, width: SUMMARY_COLS.hide, textAlign: 'right' }}>가리기</span>
        )}
    </div>
);

/**
 * 숫자 한 칸.
 * 모르는 값은 '—' 로 둡니다. 0 으로 채우면 '오늘 수업함'·'노쇼 없는 성실한 회원'이
 * 되어 위험이 통째로 가려집니다 (상세 카드와 같은 규칙).
 */
const Num = ({
    width,
    value,
    unit,
    alert,
    sub,
    title,
}: {
    width: number;
    value: string;
    unit?: string;
    alert?: boolean;
    /** 숫자 옆에 붙는 아주 작은 보조 표기 (변화율 등) */
    sub?: string;
    title?: string;
}) => (
    <span
        className="flex items-baseline justify-end"
        style={{ width, flex: 'none', gap: 2, cursor: title ? 'help' : 'inherit' }}
        title={title}
    >
        <span
            style={{
                fontSize: 15,
                fontWeight: 800,
                color: alert ? '#C22D38' : '#16202E',
                letterSpacing: '-0.02em',
                whiteSpace: 'nowrap',
            }}
        >
            {value}
        </span>
        {unit && (
            <span style={{ fontSize: 11.5, fontWeight: 600, color: alert ? '#D9737B' : '#93A0AE' }}>
                {unit}
            </span>
        )}
        {sub && (
            <span
                style={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: alert ? '#D9737B' : '#93A0AE',
                    marginLeft: 2,
                    whiteSpace: 'nowrap',
                }}
            >
                {sub}
            </span>
        )}
    </span>
);

/**
 * 지시 상태 라벨.
 *
 * "지시함" 하나로 뭉개면 **아직 안 본 지시**와 **이미 끝난 지시**가 같아 보입니다.
 * 원장님이 목록에서 판단해야 하는 것은 "또 시켜야 하나"라서, 그 구분이 곧 답입니다.
 * (ARCHIVED 는 애초에 목록에 오지 않습니다 — priorOrdersFor 가 걸러냅니다)
 *
 * 오늘의 브리핑 카드(BriefingCard)도 **이 표를 그대로** 씁니다.
 * 같은 지시가 화면마다 다른 이름으로 뜨면 원장님이 상태를 배워야 합니다.
 */
export const ORDER_STATUS: Record<OrderStatus, { label: string; bg: string; color: string }> = {
    SENT: { label: '지시함', bg: '#EEF3FB', color: '#2C4C8C' },
    SEEN: { label: '강사 확인', bg: '#EEF3FB', color: '#2C4C8C' },
    DONE: { label: '완료', bg: '#E5F8F3', color: '#00795F' },
    OVERDUE: { label: '지연', bg: '#FFE8E9', color: '#C22D38' },
    ARCHIVED: { label: '정리됨', bg: '#F1F4F9', color: '#7C8794' },
    RETRACTED: { label: '철회', bg: '#F1F4F9', color: '#7C8794' },
};

type Props = {
    m: MemberView;
    /** 지금 보고 있는 단계 — 공백 기준과 금액 색이 단계에 따라 달라진다 */
    stage: MemberStage;
    /** 그릴 수 있는 칸 — 머리글과 **같은 값**을 받아야 눈금이 안 밀린다 */
    caps: SummaryCapabilities;
    /** 목록 맨 위 줄인가 (위 테두리를 그리지 않는다) */
    first: boolean;
    /** 상세가 열려 있는 줄인가 */
    selected: boolean;
    /**
     * 이 회원 건으로 이미 나가 있는 지시.
     * 성공 토스트는 사라지므로, 보냈다는 사실은 목록에 남아 있어야 한다.
     */
    sentOrder: OrderView | null;
    /** 확인 창에서 전송 중이라 잠겨 있는가 — 목록 전체가 함께 잠긴다 */
    locked: boolean;
    /** 줄을 눌렀다 — 상세를 연다 */
    /** 줄을 눌렀다 — **카드** 식별자를 넘긴다 (한 회원에 줄이 여러 개일 수 있다) */
    onOpen: (cardKey: string) => void;
    /** 지시 **확인 창을 연다.** 여기서 바로 서버로 나가지 않는다 */
    onOrder: (m: MemberView) => void;
    /**
     * 이 회원을 회원 현황에서 가린다 (다시 보기 전까지 영구).
     * `caps.showHide` 가 false 면 부르지 않는다 — 버튼 자체를 안 그린다.
     */
    onHide: (m: MemberView) => void;
    /** 가리기 요청이 나가 있는 동안 잠근다 — 서버가 제출 순서를 보장하지 않는다 */
    hiding: boolean;
};

const MemberSummaryRow = ({
    m,
    stage,
    caps,
    first,
    selected,
    sentOrder,
    locked,
    onOpen,
    onOrder,
    onHide,
    hiding,
}: Props) => {
    const [hover, setHover] = useState(false);

    const firstPass = isFirstPass(m.passOrder);
    const needsFix = needsClassification(m.passOrder);
    const risky = stage === 'AT_RISK';
    const tags = signalTags(m, stage);
    /* 회원 단위 요약 — 없으면(구서버·단일 수강권) null 이라 아무것도 안 붙는다 */
    const rollup = summarizeRollup(m.memberRollup);
    /*
     * 지시를 보낼 수 있는가.
     * 서버는 (memberId, coachId) 가 실제 담당 관계인지 확인하고, 아니면 400 을 준다.
     * coachId 가 비어 있는 회원은 보낼 곳이 없으므로 화면에서 먼저 막는다.
     */
    const canOrder = !!m.coachId;
    /*
     * 버튼에 읽히는 이름은 상세 카드와 **같은 문장**으로 둔다.
     * 좁은 칸이라 보이는 글자는 줄이되(aria-label 이 진짜 이름이다),
     * 원장님이 상세에서 본 버튼과 목록의 버튼이 다른 일을 하는 것처럼 보이면 안 된다.
     */
    const orderName = !canOrder
        ? '담당 강사 없음 · 지시 불가'
        : sentOrder
          ? `${m.coachName} 강사에게 다시 지시하기`
          : `${m.coachName} 강사에게 지시하기`;

    const open = () => onOpen(m.cardKey);

    return (
        <div
            style={{
                borderTop: first ? 'none' : '1px solid #F1F4F9',
                background: selected ? '#F1F5FC' : hover ? '#FBFCFE' : '#FFFFFF',
                minWidth: summaryMinWidth(caps),
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            <div
                role="button"
                tabIndex={0}
                /*
                 * 이름을 못 박아 둔다. 안 그러면 줄 안의 글자가 전부 이름이 되어
                 * 안쪽 '지시하기' 버튼과 이름이 겹쳐 읽힌다.
                 */
                aria-label={`${m.name} 회원 상세 보기`}
                aria-expanded={selected}
                onClick={open}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        open();
                    }
                }}
                className="flex items-center"
                style={{
                    padding: '11px 26px',
                    gap: 10,
                    cursor: 'pointer',
                    /* 선택된 줄은 왼쪽 레일로 표시한다 — 배경색만으로는 잘 안 보인다 */
                    boxShadow: selected ? `inset 4px 0 0 ${STAGE_META[stage].dot}` : 'none',
                }}
            >
                {/* 회원 · 순번 */}
                <span className="flex min-w-0 items-center" style={{ ...NAME_COL, gap: 7 }}>
                    <span
                        style={{
                            fontSize: 15.5,
                            fontWeight: 700,
                            color: '#16202E',
                            letterSpacing: '-0.01em',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {m.name}
                    </span>
                    {/*
                      순번 뱃지 — 상세 카드와 같은 색 규칙.
                      "1번째"만 붉게 칠한다. 재등록으로 들어와 "2번째 이상"인 회원은 색이 빠진다.
                    */}
                    <span
                        className="rounded-[5px]"
                        title={`${m.passOrder.reason}\n${m.churnHint.text}`}
                        style={{
                            background: firstPass ? '#FFE8E9' : needsFix ? '#FEF3C7' : '#F1F4F9',
                            color: firstPass ? '#D92D3A' : needsFix ? '#B45309' : '#5B6B7F',
                            fontSize: 11,
                            fontWeight: 800,
                            padding: '2px 7px',
                            whiteSpace: 'nowrap',
                            flex: 'none',
                            cursor: 'help',
                        }}
                    >
                        {needsFix ? '⚠ ' : ''}
                        {m.passOrder.label}
                    </span>
                    {/*
                      휴면 보조 배지 — **두 화면의 차이를 화면이 스스로 설명한다.**

                      ─────────────────────────────────────────────────────────
                      🔴 이 회원은 원래 칸에 그대로 있습니다. 일부러입니다
                      ─────────────────────────────────────────────────────────
                      서버 브리핑은 공백 45일 초과를 **휴면**으로 따로 빼서 위험
                      명단에서 내립니다(`passOrder.DORMANT_GAP_DAYS`). 회원 현황은
                      안 그럽니다 — 여기는 전수 명단이라, 칸을 옮기면 원장님이
                      그 회원을 찾을 곳이 사라집니다.

                      그러면 "오늘의 브리핑엔 휴면이라는데 회원 현황엔 다른 칸에
                      있는" 회원이 생깁니다. 그 차이를 설명하지 않으면 원장님은
                      둘 중 하나를 고장으로 읽습니다. 그래서 칸은 그대로 두고
                      **이름 옆에서 "얘는 휴면 쪽으로 갔다"** 고 말합니다.

                      ─────────────────────────────────────────────────────────
                      왜 이탈 위험**과 주의** 둘 다인가 (2026-08-27 결정)
                      ─────────────────────────────────────────────────────────
                      서버 휴면은 두 곳에서 옵니다. `judge()` ①·①-b 에서 온 회원은
                      프론트에서 `AT_RISK`, ③(공백만 긴 회원 — 예전엔 판정 없음)에서
                      온 회원은 `WATCH` 입니다. **차이는 양쪽에 똑같이 생기므로
                      배지도 양쪽이 받습니다.** 46일 비운 회원이 주의 칸에 있으면
                      그 회원은 실제로 휴면이고, 배지가 붙는 것이 정확한 상태입니다.

                      `STABLE`·`GOLDEN` 은 제외합니다.
                        · `STABLE` 은 공백 14일 이하라야 얻는 단계여서 **도달 불가**입니다
                          (`STABLE_RULE.maxGapDays`). 조건에 넣으면 죽은 가지가 됩니다.
                        · `GOLDEN` 은 붙이면 안 됩니다. 서버도 골든타임을 휴면보다
                          **먼저** 봅니다 — 만료일은 진짜고 시한이 있어서, 휴면으로
                          덮으면 만료 임박 수강권이 조용히 만료됩니다. 화면에서도
                          재등록 상담 카드에 휴면 어조가 섞이면 메시지가 충돌합니다.

                      회색입니다 — 브리핑 카드의 휴면 배지와 **같은 색·같은 말**입니다
                      (BriefingCard 의 같은 배지 주석에 어조 근거가 있습니다).
                      옆의 「N일 공백」 배지가 이미 붉으므로, 여기까지 붉게 칠하면
                      두 달 쉰 회원이 화면에서 가장 급한 사람으로 읽힙니다.
                    */}
                    {(stage === 'AT_RISK' || stage === 'WATCH') && isDormantGap(m) && (
                        <span
                            className="rounded-[5px]"
                            title={`마지막 수업이 ${DORMANT_GAP_DAYS}일 넘게 지났습니다\n오늘의 시그널에서는 「휴면」으로 올라갑니다`}
                            style={{
                                background: '#EEF1F5',
                                color: '#5B6B7F',
                                fontSize: 11,
                                fontWeight: 800,
                                padding: '2px 7px',
                                whiteSpace: 'nowrap',
                                flex: 'none',
                                cursor: 'help',
                            }}
                        >
                            휴면
                        </span>
                    )}
                </span>

                {/* 담당 강사 · 상품 */}
                <span
                    className="flex min-w-0 flex-col"
                    style={{ width: SUMMARY_COLS.coach, flex: 'none' }}
                >
                    <span
                        style={{
                            fontSize: 13,
                            color: canOrder ? '#5B6B7F' : '#B45309',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {canOrder ? `${m.coachName} 강사` : '담당 강사 없음'}
                    </span>
                    <span
                        style={{
                            fontSize: 11.5,
                            color: '#A7B0BC',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                        /*
                          회원 단위 요약을 **툴팁으로** 붙인다 (백엔드 3차 memberRollup).
                          이 줄은 여전히 이 수강권 하나를 말한다 — 카드를 합치지 않는다.
                          좁은 칸이라 본문은 「외 N건」까지만 적고 전체 문장은 여기에 둔다.
                        */
                        title={
                            rollup
                                ? `${m.product}\n${rollup.detail}${
                                      rollup.partial ? `\n${ROLLUP_PARTIAL_NOTE}` : ''
                                  }`
                                : undefined
                        }
                    >
                        {m.product}
                        {rollup && (
                            <span style={{ color: '#7C8794', fontWeight: 700 }}>
                                {' · '}
                                {rollup.tag}
                            </span>
                        )}
                    </span>
                </span>

                {/* 신호 — 왜 이 회원이 목록에 있는가 */}
                <span className="flex min-w-0 items-center" style={{ ...SIGNAL_COL, gap: 5 }}>
                    {tags.length === 0 ? (
                        <span style={{ fontSize: 12.5, color: '#A7B0BC' }}>걸린 신호 없음</span>
                    ) : (
                        tags.slice(0, 3).map((t) => (
                            <span
                                key={t.key}
                                className="rounded-[5px]"
                                style={{
                                    background: t.tone === 'BAD' ? '#FFF0F0' : '#FFF8E8',
                                    color: t.tone === 'BAD' ? '#C22D38' : '#B45309',
                                    fontSize: 11.5,
                                    fontWeight: 700,
                                    padding: '3px 7px',
                                    whiteSpace: 'nowrap',
                                    flex: 'none',
                                }}
                            >
                                {t.text}
                            </span>
                        ))
                    )}
                    {tags.length > 3 && (
                        <span style={{ fontSize: 11.5, color: '#93A0AE', flex: 'none' }}>
                            +{tags.length - 3}
                        </span>
                    )}
                </span>

                {/*
                  남은 수업.
                  무제한(대관) 수강권은 등록상 999회다. 그대로 찍으면 원장님이
                  실제 잔여로 읽는다 — '—'(모름)와도 다른 사실이라 따로 적는다.
                */}
                <Num
                    width={SUMMARY_COLS.remain}
                    value={
                        isSessionless(m)
                            ? SESSIONLESS_REMAIN_TEXT
                            : m.remain == null
                              ? '—'
                              : String(m.remain)
                    }
                    unit={isSessionless(m) || m.remain == null ? undefined : '회'}
                    alert={isRemainAlert(m)}
                    title={
                        isSessionless(m)
                            ? SESSIONLESS_REMAIN_HINT
                            : m.remain == null
                              ? '회차 정보 없음'
                              : undefined
                    }
                />
                <Num
                    width={SUMMARY_COLS.expiry}
                    value={
                        m.expiryDday == null
                            ? '—'
                            : m.expiryDday < 0
                              ? `${Math.abs(m.expiryDday)}일 지남`
                              : `D-${m.expiryDday}`
                    }
                    alert={isExpiryAlert(m) || (m.expiryDday != null && m.expiryDday < 0)}
                    title={m.expiryDday == null ? '만료일 없음' : undefined}
                />
                <Num
                    width={SUMMARY_COLS.gap}
                    /* 0 으로 채우면 '오늘 수업함'이 되어 위험이 통째로 가려진다 */
                    value={m.lastLessonDaysAgo == null ? '기록 없음' : String(m.lastLessonDaysAgo)}
                    unit={m.lastLessonDaysAgo == null ? undefined : '일 전'}
                    alert={isGapAlert(m, stage)}
                    title={m.lastLessonDaysAgo == null ? '아직 수업한 적 없음' : undefined}
                />
                {/*
                  주당 수업.
                  서버가 주는 paceNow 는 **60일 창의 총 수업 수**라 그대로 찍으면
                  "주당 수업 10회"가 된다 (실제로는 주 1.2회). 환산은 pace.ts 한 곳에서만 한다.
                  원 수치(60일 총 수업)는 툴팁에 병기한다 — 강사와 이야기할 때 쓰는 숫자다.
                */}
                <Num
                    width={SUMMARY_COLS.pace}
                    value={weeklyPaceText(m.paceNow)}
                    unit="회"
                    sub={
                        m.paceDeltaPct == null
                            ? '비교 없음'
                            : `${m.paceDeltaPct < 0 ? '▼' : '▲'}${Math.abs(m.paceDeltaPct)}%`
                    }
                    alert={isPaceAlert(m)}
                    title={
                        m.paceDeltaPct == null
                            ? `비교할 이전 기록이 없습니다\n${paceTooltip(m.paceBefore, m.paceNow)}`
                            : paceTooltip(m.paceBefore, m.paceNow)
                    }
                />
                {/*
                  당일 취소·변경.

                  이 자리에 노쇼율이 있었다 (대표 지시로 표시에서 뺐다).
                  세 상태를 구분해서 적는다 —
                    · 숫자   재봤더니 N회
                    · '—'    못 쟀다(null). 0 으로 적으면 '한 번도 안 무른 회원'이 된다
                    · 칸 없음 서버가 이 값을 모른다 (구버전) → caps.showSameDay 가 false
                */}
                {caps.showSameDay && (
                    <Num
                        width={SUMMARY_COLS.sameDay}
                        value={m.sameDayAdjustments == null ? '—' : String(m.sameDayAdjustments)}
                        unit={m.sameDayAdjustments == null ? undefined : '회'}
                        alert={isSameDayAdjustAlert(m)}
                        title={
                            m.sameDayAdjustments == null
                                ? '최근 60일 당일 취소·변경을 세지 못했습니다'
                                : `최근 60일 당일 취소·변경 ${m.sameDayAdjustments}회 · ${SAME_DAY_ADJUST_ALERT}회부터 표시합니다`
                        }
                    />
                )}

                {/* 미사용 선수금 (+ 재등록 기대) */}
                <span
                    className="flex flex-col items-end justify-center"
                    style={{ width: SUMMARY_COLS.value, flex: 'none', cursor: 'help', gap: 2 }}
                    title={m.value ? m.value.breakdown : valueUnavailableReason(m)}
                >
                    {m.value ? (
                        <>
                            <span
                                className="rounded-[6px]"
                                style={{
                                    background: risky ? '#FFF0F0' : '#F1F5FC',
                                    color: risky ? '#D92D3A' : '#2C4C8C',
                                    fontSize: 13.5,
                                    fontWeight: 800,
                                    padding: '4px 9px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {m.value.prepaid == null ? '—' : `${shortWon(m.value.prepaid)} 원`}
                            </span>
                            {/*
                              재등록 기대는 **더하지 않고 아래에 따로** 적는다.
                              0 이면 안 그린다 — 이탈 위험·주의 자리에서 0 은 정책상 당연한
                              값이라(기대하지 않기로 한 자리) 매번 띄우면 경고처럼 읽힌다.
                            */}
                            {(m.value.expected ?? 0) > 0 && (
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: '#00795F',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    기대 {shortWon(m.value.expected as number)}
                                </span>
                            )}
                        </>
                    ) : (
                        /* 0 원이라고 쓰면 "안 챙겨도 되는 회원"이 된다. 모른다고 그대로 적는다 */
                        <span style={{ fontSize: 12, color: '#93A0AE', whiteSpace: 'nowrap' }}>
                            {VALUE_UNAVAILABLE_TEXT}
                        </span>
                    )}
                </span>

                {/* 지시 — 상태와 버튼 */}
                <span
                    className="flex items-center justify-end"
                    style={{ width: SUMMARY_COLS.order, flex: 'none', gap: 6 }}
                >
                    {sentOrder && (
                        <span
                            className="rounded-[5px]"
                            style={{
                                background: ORDER_STATUS[sentOrder.status].bg,
                                color: ORDER_STATUS[sentOrder.status].color,
                                fontSize: 11.5,
                                fontWeight: 800,
                                padding: '3px 7px',
                                whiteSpace: 'nowrap',
                                flex: 'none',
                            }}
                        >
                            {ORDER_STATUS[sentOrder.status].label}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={(e) => {
                            /* 이 버튼은 상세를 여는 것이 아니라 확인 창을 연다 */
                            e.stopPropagation();
                            onOrder(m);
                        }}
                        disabled={!canOrder || locked}
                        aria-disabled={!canOrder || locked}
                        aria-label={orderName}
                        title={
                            canOrder
                                ? orderName
                                : '담당 강사가 지정되지 않은 회원이라 지시를 보낼 수 없습니다. 회원 정보에서 담당 강사를 먼저 지정해 주세요.'
                        }
                        className="rounded-[8px]"
                        style={{
                            flex: 'none',
                            height: 32,
                            padding: '0 12px',
                            background: !canOrder || locked ? '#EFF2F7' : '#1A305D',
                            border: 'none',
                            color: !canOrder || locked ? '#93A0AE' : '#FFFFFF',
                            fontSize: 13,
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            cursor: !canOrder ? 'not-allowed' : locked ? 'progress' : 'pointer',
                        }}
                    >
                        {!canOrder ? '지시 불가' : sentOrder ? '다시 지시' : '지시하기'}
                    </button>
                </span>

                {/*
                  가리기.

                  ⚠️ 브리핑 항목 가리기와 **다른 물건**입니다. 저건 그날 그 카드 하나이고
                  이건 회원 한 명을 **다시 보기 전까지 영구히** 이 표에서 내리는 것입니다.
                  그래서 툴팁이 그 성격을 그대로 말합니다 — 원장님이 "오늘만 안 보이겠지"
                  라고 생각하고 누르면 안 됩니다.

                  ⚠️ 서버가 이 기능을 모르면(caps.showHide=false) 버튼을 아예 안 그립니다.
                  눌렀더니 404 가 나는 버튼을 만들지 않습니다.
                */}
                {caps.showHide && (
                    <span
                        className="flex items-center justify-end"
                        style={{ width: SUMMARY_COLS.hide, flex: 'none' }}
                    >
                        <button
                            type="button"
                            onClick={(e) => {
                                /* 이 버튼은 상세를 여는 것이 아니라 목록에서 내린다 */
                                e.stopPropagation();
                                onHide(m);
                            }}
                            disabled={hiding}
                            aria-disabled={hiding}
                            aria-label={`${m.name} 회원 가리기`}
                            /*
                              🔴 백엔드 v9 부터 가리기가 **브리핑 생성에서도 제외**된다
                                 (2026-08-31). 그 전에는 이 표에서만 내려갔고 다음 날
                                 아침 브리핑에는 그대로 올라왔다 — 원장이 「가렸는데
                                 왜 또 나오냐」고 묻던 자리다. 문구도 같이 바꾼다.
                            */
                            title={`${m.name} 회원을 가립니다 · 다시 보기 전까지 회원 현황과 오늘의 시그널에서 모두 제외됩니다`}
                            className="rounded-[8px]"
                            style={{
                                flex: 'none',
                                height: 30,
                                padding: '0 10px',
                                background: '#FFFFFF',
                                border: '1px solid #DCE4EE',
                                color: hiding ? '#B4BDC8' : '#5B6B7F',
                                fontSize: 12.5,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                cursor: hiding ? 'progress' : 'pointer',
                            }}
                        >
                            가리기
                        </button>
                    </span>
                )}
            </div>

            {/*
              이미 나가 있는 지시는 줄 아래에 그대로 적는다.
              서버는 중복을 막지 않는다 — 인덱스가 unique 가 아니다.
              여기서 안 알리면 같은 회원 건이 강사 화면에 두 줄로 쌓인다.
            */}
            {sentOrder && (
                <div
                    style={{
                        padding: '0 26px 10px 26px',
                        marginTop: -4,
                    }}
                >
                    <span
                        style={{
                            fontSize: 12,
                            color: '#00795F',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        ✓ {sentOrder.sentLabel}에 {sentOrder.coachName} 강사에게 지시하셨습니다 · “
                        {sentOrder.task}”
                    </span>
                </div>
            )}
        </div>
    );
};

export default MemberSummaryRow;
